import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { Scale, LogOut, Users, BookOpen, FileText, Settings, Briefcase, PenSquare, Image as ImageIcon, User, GraduationCap, Video, Calendar, Bell, Upload, MessageSquare, Send, ArrowLeft, Eye, EyeOff, Plus, Layers, Check, X, Award, Trash2, Star, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Multi-Select Batch Component
const BatchMultiSelect = ({ batches, selectedBatches, onChange, label = "Select Batches" }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleBatch = (batchCode) => {
    if (selectedBatches.includes(batchCode)) {
      onChange(selectedBatches.filter(b => b !== batchCode));
    } else {
      onChange([...selectedBatches, batchCode]);
    }
  };
  
  const toggleAll = () => {
    if (selectedBatches.includes('ALL')) {
      onChange([]);
    } else {
      onChange(['ALL']);
    }
  };
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative">
        <div 
          className="min-h-[42px] p-2 border border-slate-300 rounded-lg cursor-pointer bg-white flex flex-wrap gap-1 items-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selectedBatches.length === 0 ? (
            <span className="text-slate-400 text-sm">Click to select batches...</span>
          ) : selectedBatches.includes('ALL') ? (
            <Badge className="bg-green-100 text-green-800 border-green-300">All Batches</Badge>
          ) : (
            selectedBatches.map(code => (
              <Badge key={code} className="bg-slate-100 text-slate-800 border-slate-300">
                {code}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleBatch(code); }} />
              </Badge>
            ))
          )}
        </div>
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div 
              className={`p-2 cursor-pointer hover:bg-slate-50 flex items-center gap-2 border-b ${selectedBatches.includes('ALL') ? 'bg-green-50' : ''}`}
              onClick={toggleAll}
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedBatches.includes('ALL') ? 'bg-green-500 border-green-500' : 'border-slate-300'}`}>
                {selectedBatches.includes('ALL') && <Check className="h-3 w-3 text-white" />}
              </div>
              <span className="font-medium">All Batches</span>
            </div>
            {batches.filter(b => b.is_active).map(batch => (
              <div 
                key={batch.id}
                className={`p-2 cursor-pointer hover:bg-slate-50 flex items-center gap-2 ${selectedBatches.includes(batch.code) ? 'bg-blue-50' : ''}`}
                onClick={() => toggleBatch(batch.code)}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedBatches.includes(batch.code) ? 'bg-blue-500 border-blue-500' : 'border-slate-300'}`}>
                  {selectedBatches.includes(batch.code) && <Check className="h-3 w-3 text-white" />}
                </div>
                <div>
                  <span className="font-medium">{batch.code}</span>
                  <span className="text-slate-500 text-sm ml-2">{batch.name}</span>
                </div>
              </div>
            ))}
            {batches.filter(b => b.is_active).length === 0 && (
              <div className="p-3 text-center text-slate-500 text-sm">
                No batches available. Create batches first.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [enrollments, setEnrollments] = useState(mockData.enrollments);
  const [corporateInquiries, setCorporateInquiries] = useState([]);
  const [students, setStudents] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [batchForm, setBatchForm] = useState({
    code: '',
    name: '',
    description: '',
    start_date: '',
    is_active: true
  });
  const [studentForm, setStudentForm] = useState({
    name: '',
    phone_number: '',
    email: '',
    password: '',
    date_of_enrolment: '',
    batch: '',
    batch_code: '',
    batch_name: '',
    institution: '',
    profession: '',
    postal_address: ''
  });
  const [blogForm, setBlogForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: 'JurisGo Team',
    category: '',
    image: '',
    published: true
  });
  const [sessionForm, setSessionForm] = useState({
    title: '',
    description: '',
    batch_codes: [],
    batch_name: '',
    video_url: '',
    duration: ''
  });
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    batch_codes: [],
    batch_name: '',
    file_url: ''
  });
  const [scheduleForm, setScheduleForm] = useState({
    title: '',
    description: '',
    batch_codes: [],
    batch_name: '',
    meet_link: '',
    scheduled_date: '',
    scheduled_time: ''
  });
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    batch_codes: [],
    batch_name: 'All'
  });
  const [recordedSessions, setRecordedSessions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classSchedules, setClassSchedules] = useState([]);
  const [notificationsList, setNotificationsList] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [alumniFeedback, setAlumniFeedback] = useState([]);
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [dmForm, setDmForm] = useState({
    student_id: '',
    student_email: '',
    student_name: '',
    title: '',
    message: ''
  });
  const [selectedStudentForDM, setSelectedStudentForDM] = useState(null);
  const [showDMModal, setShowDMModal] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [contentData, setContentData] = useState({
    heroTitle: mockData.hero.title,
    heroDescription: mockData.hero.description,
    aboutDescription: mockData.about.description,
    nextBatch: mockData.enrollment.nextBatch
  });

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.email === mockData.adminUser.email && loginForm.password === mockData.adminUser.password) {
      setIsLoggedIn(true);
      fetchCorporateInquiries();
      fetchStudents();
      fetchBlogs();
      fetchBatches();
      fetchLMSData();
      fetchAlumniFeedback();
      toast({
        title: "Login Successful",
        description: "Welcome to JurisGo Admin Panel"
      });
    } else {
      toast({
        title: "Login Failed",
        description: "Invalid credentials",
        variant: "destructive"
      });
    }
  };

  const fetchCorporateInquiries = async () => {
    try {
      const response = await axios.get(`${API}/corporate-inquiries`);
      setCorporateInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs/all`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const fetchLMSData = async () => {
    try {
      const [sessionsRes, assignmentsRes, schedulesRes, notificationsRes, dmRes] = await Promise.all([
        axios.get(`${API}/recorded-sessions`),
        axios.get(`${API}/assignments`),
        axios.get(`${API}/class-schedule`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/direct-messages`)
      ]);
      
      setRecordedSessions(sessionsRes.data);
      setAssignments(assignmentsRes.data);
      setClassSchedules(schedulesRes.data);
      setNotificationsList(notificationsRes.data);
      setDirectMessages(dmRes.data);
    } catch (error) {
      console.error('Error fetching LMS data:', error);
    }
  };

  // Alumni Feedback Handlers
  const fetchAlumniFeedback = async () => {
    try {
      const response = await axios.get(`${API}/alumni-feedback`);
      setAlumniFeedback(response.data);
    } catch (error) {
      console.error('Error fetching alumni feedback:', error);
    }
  };

  const handleToggleWallOfFame = async (feedback) => {
    try {
      await axios.put(`${API}/alumni-feedback/${feedback.id}`, {
        show_on_wall_of_fame: !feedback.show_on_wall_of_fame
      });
      toast({
        title: feedback.show_on_wall_of_fame ? "Removed from Wall of Fame" : "Added to Wall of Fame",
        description: `${feedback.full_name}'s story visibility updated`
      });
      fetchAlumniFeedback();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update visibility", variant: "destructive" });
    }
  };

  const handleUpdateAlumni = async () => {
    if (!editingAlumni) return;
    try {
      await axios.put(`${API}/alumni-feedback/${editingAlumni.id}`, {
        full_name: editingAlumni.full_name,
        batch_number: editingAlumni.batch_number,
        course_feedback: editingAlumni.course_feedback,
        role_title: editingAlumni.role_title,
        organization_name: editingAlumni.organization_name,
        display_role_publicly: editingAlumni.display_role_publicly,
        consent_wall_of_fame: editingAlumni.consent_wall_of_fame,
        show_on_wall_of_fame: editingAlumni.show_on_wall_of_fame
      });
      toast({ title: "Updated", description: "Alumni response updated successfully" });
      setEditingAlumni(null);
      fetchAlumniFeedback();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update response", variant: "destructive" });
    }
  };

  const handleDeleteAlumni = async (feedbackId) => {
    if (window.confirm('Permanently delete this alumni response? This cannot be undone.')) {
      try {
        await axios.delete(`${API}/alumni-feedback/${feedbackId}`);
        toast({ title: "Deleted", description: "Alumni response permanently deleted" });
        setSelectedAlumni(null);
        fetchAlumniFeedback();
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete response", variant: "destructive" });
      }
    }
  };

  const handleExportAlumniCsv = () => {
    window.open(`${API}/alumni-feedback/export/csv`, '_blank');
  };

  // Batch Management Handlers
  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/batches`, batchForm);
      toast({
        title: "Batch Created",
        description: `Batch ${batchForm.code} has been created successfully`
      });
      setBatchForm({ code: '', name: '', description: '', start_date: '', is_active: true });
      setShowBatchForm(false);
      fetchBatches();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create batch",
        variant: "destructive"
      });
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await axios.delete(`${API}/batches/${batchId}`);
        toast({ title: "Batch Deleted", description: "Batch has been removed" });
        fetchBatches();
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete batch", variant: "destructive" });
      }
    }
  };

  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        // Update existing student
        await axios.put(`${API}/students/${editingStudent.id}`, studentForm);
        toast({
          title: "Student Updated",
          description: "Student information has been updated successfully"
        });
      } else {
        // Create new student
        await axios.post(`${API}/students`, studentForm);
        toast({
          title: "Student Added",
          description: "New student has been added successfully"
        });
      }
      
      // Reset form and refresh list
      setStudentForm({
        name: '',
        phone_number: '',
        email: '',
        password: '',
        date_of_enrolment: '',
        batch: '',
        batch_code: '',
        batch_name: '',
        institution: '',
        profession: '',
        postal_address: ''
      });
      setEditingStudent(null);
      setShowStudentForm(false);
      fetchStudents();
    } catch (error) {
      console.error('Error saving student:', error);
      toast({
        title: "Error",
        description: "Failed to save student information",
        variant: "destructive"
      });
    }
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setStudentForm({
      name: student.name,
      phone_number: student.phone_number,
      email: student.email,
      password: student.password,
      date_of_enrolment: student.date_of_enrolment,
      batch: student.batch,
      batch_name: student.batch_name,
      institution: student.institution,
      profession: student.profession,
      postal_address: student.postal_address
    });
    setShowStudentForm(true);
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await axios.delete(`${API}/students/${studentId}`);
        toast({
          title: "Student Deleted",
          description: "Student has been removed successfully"
        });
        fetchStudents();
      } catch (error) {
        console.error('Error deleting student:', error);
        toast({
          title: "Error",
          description: "Failed to delete student",
          variant: "destructive"
        });
      }
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog.id}`, blogForm);
        toast({
          title: "Blog Updated",
          description: "Blog post has been updated successfully"
        });
      } else {
        await axios.post(`${API}/blogs`, blogForm);
        toast({
          title: "Blog Published",
          description: "New blog post has been published successfully"
        });
      }
      
      setBlogForm({
        title: '',
        excerpt: '',
        content: '',
        author: 'JurisGo Team',
        category: '',
        image: '',
        published: true
      });
      setEditingBlog(null);
      setShowBlogForm(false);
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast({
        title: "Error",
        description: "Failed to save blog post",
        variant: "destructive"
      });
    }
  };

  const handleEditBlog = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      author: blog.author,
      category: blog.category,
      image: blog.image,
      published: blog.published
    });
    setShowBlogForm(true);
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await axios.delete(`${API}/blogs/${blogId}`);
        toast({
          title: "Blog Deleted",
          description: "Blog post has been removed successfully"
        });
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
        toast({
          title: "Error",
          description: "Failed to delete blog",
          variant: "destructive"
        });
      }
    }
  };

  const updateInquiryStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/corporate-inquiry/${id}/status?status=${newStatus}`);
      setCorporateInquiries(corporateInquiries.map(inquiry => 
        inquiry.id === id ? { ...inquiry, status: newStatus } : inquiry
      ));
      toast({
        title: "Status Updated",
        description: `Inquiry status changed to ${newStatus}`
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginForm({ email: '', password: '' });
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully"
    });
  };

  // LMS Management Functions
  const handleSessionSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recorded-sessions`, sessionForm);
      toast({
        title: "Session Added",
        description: "Recorded session has been added successfully"
      });
      setSessionForm({ title: '', description: '', batch_name: '', video_url: '', duration: '' });
      fetchLMSData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add session",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await axios.delete(`${API}/recorded-sessions/${sessionId}`);
        toast({
          title: "Session Deleted",
          description: "Recorded session has been removed"
        });
        fetchLMSData();
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete session",
          variant: "destructive"
        });
      }
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/assignments`, assignmentForm);
      toast({
        title: "Assignment Added",
        description: "Assignment has been added successfully"
      });
      setAssignmentForm({ title: '', description: '', batch_name: '', file_url: '' });
      fetchLMSData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add assignment",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        await axios.delete(`${API}/assignments/${assignmentId}`);
        toast({
          title: "Assignment Deleted",
          description: "Assignment has been removed"
        });
        fetchLMSData();
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete assignment",
          variant: "destructive"
        });
      }
    }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/class-schedule`, scheduleForm);
      toast({
        title: "Class Scheduled",
        description: "Class has been added to schedule"
      });
      setScheduleForm({ title: '', description: '', batch_name: '', meet_link: '', scheduled_date: '', scheduled_time: '' });
      fetchLMSData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add schedule",
        variant: "destructive"
      });
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm('Are you sure you want to delete this class schedule?')) {
      try {
        await axios.delete(`${API}/class-schedule/${scheduleId}`);
        toast({
          title: "Schedule Deleted",
          description: "Class schedule has been removed"
        });
        fetchLMSData();
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete schedule",
          variant: "destructive"
        });
      }
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notifications`, notificationForm);
      toast({
        title: "Notification Sent",
        description: "Notification has been sent to students"
      });
      setNotificationForm({ title: '', message: '', batch_name: 'All' });
      fetchLMSData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send notification",
        variant: "destructive"
      });
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await axios.delete(`${API}/notifications/${notificationId}`);
        toast({
          title: "Notification Deleted",
          description: "Notification has been removed"
        });
        fetchLMSData();
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete notification",
          variant: "destructive"
        });
      }
    }
  };

  // Video Upload Handler
  // Cloudinary Video Upload Handler with Chunked Upload for large files (up to 800MB)
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Error", description: "Only MP4, MOV, AVI, WEBM files are allowed", variant: "destructive" });
      return;
    }
    
    // Check file size (800 MB limit)
    const maxSize = 800 * 1024 * 1024; // 800 MB in bytes
    if (file.size > maxSize) {
      toast({ title: "Error", description: "File size must be less than 800 MB", variant: "destructive" });
      return;
    }
    
    setUploadingVideo(true);
    setVideoUploadProgress(0);
    
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    
    // For files larger than 100MB, use chunked upload
    const chunkSize = 20 * 1024 * 1024; // 20 MB chunks
    const useChunkedUpload = file.size > 100 * 1024 * 1024;
    
    try {
      if (useChunkedUpload) {
        // Chunked upload for large files
        const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const totalChunks = Math.ceil(file.size / chunkSize);
        let uploadedBytes = 0;
        
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('file', chunk);
          formData.append('upload_preset', uploadPreset);
          formData.append('folder', 'jurisgo/videos');
          formData.append('public_id', uniqueId);
          
          // Add chunked upload headers
          const headers = {
            'X-Unique-Upload-Id': uniqueId,
            'Content-Range': `bytes ${start}-${end - 1}/${file.size}`
          };
          
          const response = await axios.post(
            `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
            formData,
            { headers }
          );
          
          uploadedBytes += (end - start);
          const progress = Math.round((uploadedBytes / file.size) * 100);
          setVideoUploadProgress(progress);
          
          // Last chunk will have the final response
          if (chunkIndex === totalChunks - 1) {
            const videoUrl = response.data.secure_url;
            setSessionForm({ ...sessionForm, video_url: videoUrl });
            toast({ title: "Video Uploaded", description: `${file.name} uploaded successfully (${Math.round(file.size / 1024 / 1024)} MB)` });
          }
        }
      } else {
        // Standard upload for smaller files
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', uploadPreset);
        formData.append('folder', 'jurisgo/videos');
        
        const uploadRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
          formData,
          {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setVideoUploadProgress(progress);
            }
          }
        );
        
        const videoUrl = uploadRes.data.secure_url;
        setSessionForm({ ...sessionForm, video_url: videoUrl });
        toast({ title: "Video Uploaded", description: `${file.name} uploaded successfully` });
      }
    } catch (error) {
      console.error('Video upload error:', error);
      const errorMsg = error.response?.data?.error?.message || error.message || "Failed to upload video";
      toast({ title: "Upload Failed", description: errorMsg, variant: "destructive" });
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(0);
    }
  };

  // Cloudinary PDF Upload Handler (using unsigned preset)
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast({ title: "Error", description: "Only PDF files are allowed", variant: "destructive" });
      return;
    }
    
    setUploadingPdf(true);
    setPdfUploadProgress(0);
    
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    
    try {
      // Upload directly to Cloudinary using unsigned preset
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', 'jurisgo/pdfs');
      
      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/raw/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setPdfUploadProgress(progress);
          }
        }
      );
      
      // Get the secure URL
      const pdfUrl = uploadRes.data.secure_url;
      setAssignmentForm({ ...assignmentForm, file_url: pdfUrl });
      toast({ title: "PDF Uploaded", description: `${file.name} uploaded successfully to cloud` });
    } catch (error) {
      console.error('PDF upload error:', error);
      const errorMsg = error.response?.data?.error?.message || error.message || "Failed to upload PDF";
      toast({ title: "Upload Failed", description: errorMsg, variant: "destructive" });
    } finally {
      setUploadingPdf(false);
      setPdfUploadProgress(0);
    }
  };

  // Direct Message Handlers
  const openDMModal = (student) => {
    setSelectedStudentForDM(student);
    setDmForm({
      student_id: student.id,
      student_email: student.email,
      student_name: student.name,
      title: '',
      message: ''
    });
    setShowDMModal(true);
  };

  const handleSendDM = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/direct-messages`, dmForm);
      toast({
        title: "Message Sent",
        description: `Message sent to ${dmForm.student_name}`
      });
      setShowDMModal(false);
      setDmForm({ student_id: '', student_email: '', student_name: '', title: '', message: '' });
      fetchLMSData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    }
  };

  const handleDeleteDM = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await axios.delete(`${API}/direct-messages/${messageId}`);
        toast({ title: "Message Deleted", description: "Message has been removed" });
        fetchLMSData();
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete message", variant: "destructive" });
      }
    }
  };

  const updateEnrollmentStatus = (id, newStatus) => {
    setEnrollments(enrollments.map(enrollment => 
      enrollment.id === id ? { ...enrollment, status: newStatus } : enrollment
    ));
    toast({
      title: "Status Updated",
      description: `Enrollment status changed to ${newStatus}`
    });
  };

  const saveContent = () => {
    toast({
      title: "Content Saved",
      description: "Website content has been updated successfully"
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex">
        {/* Left Side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 flex flex-col justify-center px-16 text-white">
            <div className="mb-12">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-16 w-16 object-contain mb-6" />
              <h1 className="text-4xl font-light tracking-tight mb-4">
                Admin Portal
              </h1>
              <p className="text-lg text-white/70 font-light leading-relaxed max-w-md">
                Manage students, content, and your learning management system from one central dashboard.
              </p>
            </div>
            <div className="space-y-6 border-t border-white/10 pt-8">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Users className="h-5 w-5" />
                </div>
                <span className="text-white/70">Student management & enrollment</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <span className="text-white/70">Learning content administration</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <PenSquare className="h-5 w-5" />
                </div>
                <span className="text-white/70">Blog & content publishing</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex flex-col">
          {/* Back Button */}
          <div className="p-6">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm"
              data-testid="back-to-website-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to website
            </button>
          </div>

          {/* Form Container */}
          <div className="flex-1 flex items-center justify-center px-8 lg:px-16">
            <div className="w-full max-w-sm">
              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center gap-3 mb-10">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-10 w-10 object-contain" />
                <span className="text-xl font-semibold text-slate-900">JurisGo</span>
              </div>

              <div className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                  Admin Login
                </h2>
                <p className="text-slate-500 text-sm">
                  Enter your credentials to access the admin dashboard
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@jurisgo.in"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    className="h-12 bg-white border-slate-200 focus:border-slate-900 focus:ring-0 rounded-lg"
                    data-testid="admin-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="h-12 bg-white border-slate-200 focus:border-slate-900 focus:ring-0 rounded-lg pr-12"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-all duration-200"
                  data-testid="admin-login-submit-btn"
                >
                  Sign In to Dashboard
                </Button>
              </form>

              <p className="mt-8 text-center text-sm text-slate-500">
                Need help? Contact{' '}
                <a href="mailto:jurisgo.contact@gmail.com" className="text-slate-900 hover:underline">
                  support
                </a>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 text-center">
            <p className="text-xs text-slate-400">
              JurisGo Admin &copy; {new Date().getFullYear()}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Admin Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-7 w-7 object-contain" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">JurisGo Admin</span>
                <p className="text-xs text-slate-300">Content Management System</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')} className="border-slate-600 text-white hover:bg-white/10">
                View Website
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="students" className="space-y-6">
          <TabsList className="grid w-full max-w-7xl grid-cols-9 bg-gradient-to-r from-slate-100 to-slate-50">
            <TabsTrigger value="students" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Students
            </TabsTrigger>
            <TabsTrigger value="batches" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Layers className="h-4 w-4 mr-2" />
              Batches
            </TabsTrigger>
            <TabsTrigger value="lms" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <GraduationCap className="h-4 w-4 mr-2" />
              LMS
            </TabsTrigger>
            <TabsTrigger value="blogs" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <PenSquare className="h-4 w-4 mr-2" />
              Blogs
            </TabsTrigger>
            <TabsTrigger value="enrollments" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Enrollments
            </TabsTrigger>
            <TabsTrigger value="corporate" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Briefcase className="h-4 w-4 mr-2" />
              Corporate
            </TabsTrigger>
            <TabsTrigger value="alumni" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="alumni-tab-trigger">
              <Award className="h-4 w-4 mr-2" />
              Alumni
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Batches Tab */}
          <TabsContent value="batches" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Batch Management</CardTitle>
                  <CardDescription>Create and manage batch codes for student groups</CardDescription>
                </div>
                <Button onClick={() => setShowBatchForm(!showBatchForm)} className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Batch
                </Button>
              </CardHeader>
              <CardContent>
                {showBatchForm && (
                  <form onSubmit={handleBatchSubmit} className="bg-slate-50 p-4 rounded-lg mb-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="batch_code">Batch Code *</Label>
                        <Input
                          id="batch_code"
                          placeholder="e.g., PI-FEB-2026"
                          value={batchForm.code}
                          onChange={(e) => setBatchForm({...batchForm, code: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="batch_name">Batch Name *</Label>
                        <Input
                          id="batch_name"
                          placeholder="e.g., Personal Injury - February 2026"
                          value={batchForm.name}
                          onChange={(e) => setBatchForm({...batchForm, name: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="batch_start">Start Date</Label>
                        <Input
                          id="batch_start"
                          type="date"
                          value={batchForm.start_date}
                          onChange={(e) => setBatchForm({...batchForm, start_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="batch_desc">Description</Label>
                        <Input
                          id="batch_desc"
                          placeholder="Brief description"
                          value={batchForm.description}
                          onChange={(e) => setBatchForm({...batchForm, description: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-slate-900">Create Batch</Button>
                      <Button type="button" variant="outline" onClick={() => setShowBatchForm(false)}>Cancel</Button>
                    </div>
                  </form>
                )}
                
                <div className="space-y-3">
                  {batches.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Layers className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No batches created yet. Create your first batch to get started.</p>
                    </div>
                  ) : (
                    batches.map(batch => (
                      <div key={batch.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-4">
                          <Badge className={batch.is_active ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}>
                            {batch.code}
                          </Badge>
                          <div>
                            <p className="font-medium text-slate-900">{batch.name}</p>
                            <p className="text-sm text-slate-500">{batch.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {batch.start_date && (
                            <span className="text-sm text-slate-500">Starts: {batch.start_date}</span>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDeleteBatch(batch.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Student Management</CardTitle>
                    <CardDescription>Add and manage student records and details</CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setShowStudentForm(!showStudentForm);
                      setEditingStudent(null);
                      setStudentForm({
                        name: '',
                        phone_number: '',
                        email: '',
                        date_of_enrolment: '',
                        batch: '',
                        batch_name: '',
                        institution: '',
                        profession: '',
                        postal_address: ''
                      });
                    }}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    {showStudentForm ? 'Cancel' : 'Add New Student'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showStudentForm && (
                  <form onSubmit={handleStudentSubmit} className="mb-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      {editingStudent ? 'Edit Student' : 'Add New Student'}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={studentForm.name}
                          onChange={(e) => setStudentForm({...studentForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone_number">Phone Number *</Label>
                        <Input
                          id="phone_number"
                          value={studentForm.phone_number}
                          onChange={(e) => setStudentForm({...studentForm, phone_number: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email ID (Username) *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={studentForm.email}
                          onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Portal Password *</Label>
                        <Input
                          id="password"
                          type="text"
                          placeholder="Set student portal password"
                          value={studentForm.password}
                          onChange={(e) => setStudentForm({...studentForm, password: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="date_of_enrolment">Date of Enrolment *</Label>
                        <Input
                          id="date_of_enrolment"
                          type="date"
                          value={studentForm.date_of_enrolment}
                          onChange={(e) => setStudentForm({...studentForm, date_of_enrolment: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="batch">Batch *</Label>
                        <Input
                          id="batch"
                          placeholder="e.g., Batch 1, Batch 2"
                          value={studentForm.batch}
                          onChange={(e) => setStudentForm({...studentForm, batch: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="batch_code">Batch Code *</Label>
                        <select
                          id="batch_code"
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                          value={studentForm.batch_code}
                          onChange={(e) => {
                            const selectedBatch = batches.find(b => b.code === e.target.value);
                            setStudentForm({
                              ...studentForm, 
                              batch_code: e.target.value,
                              batch_name: selectedBatch ? selectedBatch.name : ''
                            });
                          }}
                          required
                        >
                          <option value="">Select a batch...</option>
                          {batches.filter(b => b.is_active).map(batch => (
                            <option key={batch.id} value={batch.code}>{batch.code} - {batch.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="institution">Institution *</Label>
                        <Input
                          id="institution"
                          value={studentForm.institution}
                          onChange={(e) => setStudentForm({...studentForm, institution: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profession">Profession *</Label>
                        <Input
                          id="profession"
                          value={studentForm.profession}
                          onChange={(e) => setStudentForm({...studentForm, profession: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="postal_address">Postal Address *</Label>
                        <Textarea
                          id="postal_address"
                          value={studentForm.postal_address}
                          onChange={(e) => setStudentForm({...studentForm, postal_address: e.target.value})}
                          required
                          rows={3}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                        {editingStudent ? 'Update Student' : 'Add Student'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setShowStudentForm(false);
                          setEditingStudent(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-4">
                  {students.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No students added yet</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-slate-100 border-b border-slate-200">
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Name</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Email</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Phone</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Batch</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Institution</th>
                            <th className="text-left p-3 text-sm font-semibold text-slate-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((student) => (
                            <tr key={student.id} className="border-b border-slate-200 hover:bg-slate-50">
                              <td className="p-3 text-sm text-slate-900">{student.name}</td>
                              <td className="p-3 text-sm text-slate-600">{student.email}</td>
                              <td className="p-3 text-sm text-slate-600">{student.phone_number}</td>
                              <td className="p-3 text-sm text-slate-600">{student.batch_name}</td>
                              <td className="p-3 text-sm text-slate-600">{student.institution}</td>
                              <td className="p-3 text-sm">
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => openDMModal(student)}
                                    className="border-blue-200 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    data-testid={`message-student-${student.id}`}
                                  >
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    Message
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleEditStudent(student)}
                                    data-testid={`edit-student-${student.id}`}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleDeleteStudent(student.id)}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`delete-student-${student.id}`}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Learning Management System Tab */}
          <TabsContent value="lms" className="space-y-4">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Upload Session Recording */}
              <Card className="border-slate-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                  <CardTitle className="flex items-center text-lg">
                    <Video className="h-5 w-5 mr-2 text-slate-700" />
                    Upload Session Recording
                  </CardTitle>
                  <CardDescription>Add recorded sessions for students</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSessionSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="session_title">Session Title *</Label>
                      <Input
                        id="session_title"
                        value={sessionForm.title}
                        onChange={(e) => setSessionForm({...sessionForm, title: e.target.value})}
                        required
                        data-testid="session-title-input"
                      />
                    </div>
                    <BatchMultiSelect
                      batches={batches}
                      selectedBatches={sessionForm.batch_codes}
                      onChange={(codes) => setSessionForm({...sessionForm, batch_codes: codes})}
                      label="Select Batches *"
                    />
                    <div className="space-y-2">
                      <Label>Upload Video File (MP4) *</Label>
                      <input 
                        type="file" 
                        ref={videoInputRef}
                        accept=".mp4,.mov,.avi,.webm"
                        onChange={handleVideoUpload}
                        className="hidden"
                        data-testid="video-file-input"
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                          className="flex-1"
                          data-testid="upload-video-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingVideo ? `Uploading... ${videoUploadProgress}%` : 'Choose Video File (up to 800MB)'}
                        </Button>
                      </div>
                      {uploadingVideo && (
                        <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-slate-900 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${videoUploadProgress}%` }}
                          ></div>
                        </div>
                      )}
                      {sessionForm.video_url && !uploadingVideo && (
                        <p className="text-xs text-green-600 mt-1">Video uploaded to cloud successfully</p>
                      )}
                      <p className="text-xs text-slate-500">Or paste a video URL below:</p>
                      <Input
                        id="session_url"
                        type="url"
                        placeholder="https://example.com/video.mp4"
                        value={sessionForm.video_url}
                        onChange={(e) => setSessionForm({...sessionForm, video_url: e.target.value})}
                        data-testid="session-url-input"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="session_duration">Duration</Label>
                        <Input
                          id="session_duration"
                          placeholder="e.g., 45 mins"
                          value={sessionForm.duration}
                          onChange={(e) => setSessionForm({...sessionForm, duration: e.target.value})}
                          data-testid="session-duration-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="session_desc">Description</Label>
                      <Textarea
                        id="session_desc"
                        rows={3}
                        value={sessionForm.description}
                        onChange={(e) => setSessionForm({...sessionForm, description: e.target.value})}
                        data-testid="session-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-slate-900 to-slate-800" data-testid="add-session-btn">
                      Add Session
                    </Button>
                  </form>
                  
                  <div className="mt-6 space-y-2">
                    <h4 className="font-semibold text-sm text-slate-700">Recorded Sessions ({recordedSessions.length})</h4>
                    {recordedSessions.length === 0 ? (
                      <p className="text-sm text-slate-500">No sessions uploaded yet</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {recordedSessions.map((session) => (
                          <div key={session.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{session.title}</p>
                              <p className="text-xs text-slate-500">{session.batch_name}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSession(session.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upload Assignment */}
              <Card className="border-slate-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                  <CardTitle className="flex items-center text-lg">
                    <FileText className="h-5 w-5 mr-2 text-slate-700" />
                    Upload Assignment
                  </CardTitle>
                  <CardDescription>Add assignments for students</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleAssignmentSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="assignment_title">Assignment Title *</Label>
                      <Input
                        id="assignment_title"
                        value={assignmentForm.title}
                        onChange={(e) => setAssignmentForm({...assignmentForm, title: e.target.value})}
                        required
                        data-testid="assignment-title-input"
                      />
                    </div>
                    <BatchMultiSelect
                      batches={batches}
                      selectedBatches={assignmentForm.batch_codes}
                      onChange={(codes) => setAssignmentForm({...assignmentForm, batch_codes: codes})}
                      label="Select Batches *"
                    />
                    <div className="space-y-2">
                      <Label>Upload PDF File *</Label>
                      <input 
                        type="file" 
                        ref={pdfInputRef}
                        accept=".pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                        data-testid="pdf-file-input"
                      />
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => pdfInputRef.current?.click()}
                          disabled={uploadingPdf}
                          className="flex-1"
                          data-testid="upload-pdf-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingPdf ? `Uploading... ${pdfUploadProgress}%` : 'Choose PDF File'}
                        </Button>
                      </div>
                      {uploadingPdf && (
                        <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-slate-900 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${pdfUploadProgress}%` }}
                          ></div>
                        </div>
                      )}
                      {assignmentForm.file_url && !uploadingPdf && (
                        <p className="text-xs text-green-600 mt-1">PDF uploaded to cloud successfully</p>
                      )}
                      <p className="text-xs text-slate-500">Or paste a PDF URL below:</p>
                      <Input
                        id="assignment_url"
                        type="url"
                        placeholder="https://example.com/assignment.pdf"
                        value={assignmentForm.file_url}
                        onChange={(e) => setAssignmentForm({...assignmentForm, file_url: e.target.value})}
                        data-testid="assignment-url-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assignment_desc">Description</Label>
                      <Textarea
                        id="assignment_desc"
                        rows={3}
                        value={assignmentForm.description}
                        onChange={(e) => setAssignmentForm({...assignmentForm, description: e.target.value})}
                        data-testid="assignment-description-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-slate-900 to-slate-800" data-testid="add-assignment-btn">
                      Add Assignment
                    </Button>
                  </form>
                  
                  <div className="mt-6 space-y-2">
                    <h4 className="font-semibold text-sm text-slate-700">Assignments ({assignments.length})</h4>
                    {assignments.length === 0 ? (
                      <p className="text-sm text-slate-500">No assignments uploaded yet</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {assignments.map((assignment) => (
                          <div key={assignment.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{assignment.title}</p>
                              <p className="text-xs text-slate-500">{assignment.batch_name}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAssignment(assignment.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Add Class Schedule */}
              <Card className="border-slate-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                  <CardTitle className="flex items-center text-lg">
                    <Calendar className="h-5 w-5 mr-2 text-slate-700" />
                    Add Class Schedule
                  </CardTitle>
                  <CardDescription>Schedule upcoming live classes</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleScheduleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="schedule_title">Class Title *</Label>
                      <Input
                        id="schedule_title"
                        value={scheduleForm.title}
                        onChange={(e) => setScheduleForm({...scheduleForm, title: e.target.value})}
                        required
                      />
                    </div>
                    <BatchMultiSelect
                      batches={batches}
                      selectedBatches={scheduleForm.batch_codes}
                      onChange={(codes) => setScheduleForm({...scheduleForm, batch_codes: codes})}
                      label="Select Batches *"
                    />
                    <div className="space-y-2">
                      <Label htmlFor="schedule_meet">Google Meet Link *</Label>
                      <Input
                        id="schedule_meet"
                        type="url"
                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                        value={scheduleForm.meet_link}
                        onChange={(e) => setScheduleForm({...scheduleForm, meet_link: e.target.value})}
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="schedule_date">Date *</Label>
                        <Input
                          id="schedule_date"
                          type="date"
                          value={scheduleForm.scheduled_date}
                          onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="schedule_time">Time *</Label>
                        <Input
                          id="schedule_time"
                          type="time"
                          value={scheduleForm.scheduled_time}
                          onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_desc">Description</Label>
                      <Textarea
                        id="schedule_desc"
                        rows={2}
                        value={scheduleForm.description}
                        onChange={(e) => setScheduleForm({...scheduleForm, description: e.target.value})}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-slate-900 to-slate-800">
                      Add to Schedule
                    </Button>
                  </form>
                  
                  <div className="mt-6 space-y-2">
                    <h4 className="font-semibold text-sm text-slate-700">Scheduled Classes ({classSchedules.length})</h4>
                    {classSchedules.length === 0 ? (
                      <p className="text-sm text-slate-500">No classes scheduled yet</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {classSchedules.map((schedule) => (
                          <div key={schedule.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{schedule.title}</p>
                              <p className="text-xs text-slate-500">{schedule.batch_name} • {schedule.scheduled_date} at {schedule.scheduled_time}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Send Notification */}
              <Card className="border-slate-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                  <CardTitle className="flex items-center text-lg">
                    <Bell className="h-5 w-5 mr-2 text-slate-700" />
                    Send Notification
                  </CardTitle>
                  <CardDescription>Send announcements to students</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleNotificationSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="notification_title">Notification Title *</Label>
                      <Input
                        id="notification_title"
                        value={notificationForm.title}
                        onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                        required
                      />
                    </div>
                    <BatchMultiSelect
                      batches={batches}
                      selectedBatches={notificationForm.batch_codes}
                      onChange={(codes) => setNotificationForm({...notificationForm, batch_codes: codes})}
                      label="Send To *"
                    />
                    <div className="space-y-2">
                      <Label htmlFor="notification_message">Message *</Label>
                      <Textarea
                        id="notification_message"
                        rows={4}
                        placeholder="Enter your announcement message..."
                        value={notificationForm.message}
                        onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-slate-900 to-slate-800">
                      Send Notification
                    </Button>
                  </form>
                  
                  <div className="mt-6 space-y-2">
                    <h4 className="font-semibold text-sm text-slate-700">Recent Notifications ({notificationsList.length})</h4>
                    {notificationsList.length === 0 ? (
                      <p className="text-sm text-slate-500">No notifications sent yet</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {notificationsList.slice(0, 5).map((notification) => (
                          <div key={notification.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{notification.title}</p>
                              <p className="text-xs text-slate-500">{notification.batch_name} • {new Date(notification.created_at).toLocaleDateString()}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteNotification(notification.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Blog Management Tab */}
          <TabsContent value="blogs" className="space-y-4">
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl text-slate-900">Blog Management</CardTitle>
                    <CardDescription className="text-slate-600">Create, edit, and manage blog posts on your website</CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      setShowBlogForm(!showBlogForm);
                      setEditingBlog(null);
                      setBlogForm({
                        title: '',
                        excerpt: '',
                        content: '',
                        author: 'JurisGo Team',
                        category: '',
                        image: '',
                        published: true
                      });
                    }}
                    className="bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 shadow-md"
                  >
                    <PenSquare className="h-4 w-4 mr-2" />
                    {showBlogForm ? 'Cancel' : 'New Blog Post'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {showBlogForm && (
                  <form onSubmit={handleBlogSubmit} className="mb-8 p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl border-2 border-slate-200 shadow-sm">
                    <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                      <PenSquare className="h-6 w-6 mr-2 text-slate-700" />
                      {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
                    </h3>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="blog_title" className="text-slate-700 font-semibold">Title *</Label>
                        <Input
                          id="blog_title"
                          placeholder="Enter blog post title"
                          value={blogForm.title}
                          onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                          required
                          className="h-12 text-lg border-slate-300"
                        />
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="blog_author" className="text-slate-700 font-semibold">Author *</Label>
                          <Input
                            id="blog_author"
                            value={blogForm.author}
                            onChange={(e) => setBlogForm({...blogForm, author: e.target.value})}
                            required
                            className="h-11 border-slate-300"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="blog_category" className="text-slate-700 font-semibold">Category *</Label>
                          <Input
                            id="blog_category"
                            placeholder="e.g., Personal Injury, Career Tips"
                            value={blogForm.category}
                            onChange={(e) => setBlogForm({...blogForm, category: e.target.value})}
                            required
                            className="h-11 border-slate-300"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="blog_excerpt" className="text-slate-700 font-semibold">Excerpt *</Label>
                        <Textarea
                          id="blog_excerpt"
                          placeholder="Brief summary of the blog post (will appear in blog cards)"
                          value={blogForm.excerpt}
                          onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                          required
                          rows={3}
                          className="border-slate-300"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="blog_content" className="text-slate-700 font-semibold">Content *</Label>
                        <Textarea
                          id="blog_content"
                          placeholder="Full blog post content..."
                          value={blogForm.content}
                          onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                          required
                          rows={10}
                          className="border-slate-300 font-mono text-sm"
                        />
                        <p className="text-xs text-slate-500">Write your blog content here. You can use plain text or HTML formatting.</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="blog_image" className="text-slate-700 font-semibold flex items-center">
                          <ImageIcon className="h-4 w-4 mr-1" />
                          Cover Image URL *
                        </Label>
                        <Input
                          id="blog_image"
                          type="url"
                          placeholder="https://example.com/image.jpg"
                          value={blogForm.image}
                          onChange={(e) => setBlogForm({...blogForm, image: e.target.value})}
                          required
                          className="h-11 border-slate-300"
                        />
                        <p className="text-xs text-slate-500">Paste the URL of your blog cover image. Use Unsplash or upload to an image hosting service.</p>
                      </div>

                      <div className="flex items-center space-x-2 p-4 bg-slate-100 rounded-lg">
                        <input
                          type="checkbox"
                          id="blog_published"
                          checked={blogForm.published}
                          onChange={(e) => setBlogForm({...blogForm, published: e.target.checked})}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="blog_published" className="text-slate-700 font-medium cursor-pointer">
                          Publish immediately (uncheck to save as draft)
                        </Label>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-8">
                      <Button type="submit" className="bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 shadow-md px-8">
                        {editingBlog ? 'Update Blog Post' : 'Publish Blog Post'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setShowBlogForm(false);
                          setEditingBlog(null);
                        }}
                        className="border-slate-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-4">
                  {blogs.length === 0 ? (
                    <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                      <PenSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 text-lg">No blog posts yet</p>
                      <p className="text-slate-400 text-sm mt-1">Create your first blog post to get started</p>
                    </div>
                  ) : (
                    blogs.map((blog) => (
                      <Card key={blog.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex gap-6">
                            {/* Blog Image */}
                            <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                              <img 
                                src={blog.image} 
                                alt={blog.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Blog Content */}
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-xl font-bold text-slate-900">{blog.title}</h3>
                                    {!blog.published && (
                                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Draft</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
                                    <span className="flex items-center">
                                      <User className="h-3 w-3 mr-1" />
                                      {blog.author}
                                    </span>
                                    <span>•</span>
                                    <Badge variant="outline" className="text-xs">{blog.category}</Badge>
                                    <span>•</span>
                                    <span>{new Date(blog.created_at).toLocaleDateString()}</span>
                                  </div>
                                  <p className="text-slate-600 line-clamp-2">{blog.excerpt}</p>
                                </div>
                              </div>
                              
                              <div className="flex gap-2 mt-4">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditBlog(blog)}
                                  className="border-slate-300"
                                >
                                  <PenSquare className="h-3 w-3 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDeleteBlog(blog.id)}
                                  className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Enrollment Requests</CardTitle>
                <CardDescription>Manage student enrollments and applications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {enrollments.map((enrollment) => (
                    <div key={enrollment.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-slate-900">{enrollment.name}</h3>
                            <Badge variant={enrollment.status === 'Approved' ? 'default' : 'secondary'}>
                              {enrollment.status}
                            </Badge>
                          </div>
                          <div className="space-y-1 text-sm text-slate-600">
                            <p>Email: {enrollment.email}</p>
                            <p>Phone: {enrollment.phone}</p>
                            <p>Education: {enrollment.education}</p>
                            <p className="text-slate-500">Applied: {enrollment.date}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateEnrollmentStatus(enrollment.id, 'Approved')}
                            disabled={enrollment.status === 'Approved'}
                          >
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => updateEnrollmentStatus(enrollment.id, 'Rejected')}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Corporate Inquiries Tab */}
          <TabsContent value="corporate" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Corporate Inquiries</CardTitle>
                <CardDescription>Manage corporate service inquiries from law firms and businesses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {corporateInquiries.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No corporate inquiries yet</p>
                  ) : (
                    corporateInquiries.map((inquiry) => (
                      <div key={inquiry.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">{inquiry.company_name}</h3>
                              <p className="text-sm text-slate-500">
                                {new Date(inquiry.timestamp).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <Badge variant={inquiry.status === 'Contacted' ? 'default' : inquiry.status === 'Pending' ? 'secondary' : 'outline'}>
                              {inquiry.status}
                            </Badge>
                          </div>
                          
                          <div className="grid md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="font-medium text-slate-700">Contact Person:</span>
                              <p className="text-slate-600">{inquiry.contact_name}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Service:</span>
                              <p className="text-slate-600">{inquiry.service}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Email:</span>
                              <p className="text-slate-600">{inquiry.email}</p>
                            </div>
                            <div>
                              <span className="font-medium text-slate-700">Phone:</span>
                              <p className="text-slate-600">{inquiry.phone}</p>
                            </div>
                          </div>
                          
                          <div>
                            <span className="font-medium text-slate-700 text-sm">Message:</span>
                            <p className="text-slate-600 text-sm mt-1 bg-slate-50 p-3 rounded">{inquiry.message}</p>
                          </div>
                          
                          <div className="flex gap-2 pt-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateInquiryStatus(inquiry.id, 'Contacted')}
                              disabled={inquiry.status === 'Contacted'}
                            >
                              Mark Contacted
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => updateInquiryStatus(inquiry.id, 'Closed')}
                            >
                              Close
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Website Content Management</CardTitle>
                <CardDescription>Edit homepage content and settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="heroTitle">Hero Section Title</Label>
                  <Input
                    id="heroTitle"
                    value={contentData.heroTitle}
                    onChange={(e) => setContentData({ ...contentData, heroTitle: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="heroDescription">Hero Section Description</Label>
                  <Textarea
                    id="heroDescription"
                    value={contentData.heroDescription}
                    onChange={(e) => setContentData({ ...contentData, heroDescription: e.target.value })}
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="aboutDescription">About Section Description</Label>
                  <Textarea
                    id="aboutDescription"
                    value={contentData.aboutDescription}
                    onChange={(e) => setContentData({ ...contentData, aboutDescription: e.target.value })}
                    rows={4}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="nextBatch">Next Batch Information</Label>
                  <Input
                    id="nextBatch"
                    value={contentData.nextBatch}
                    onChange={(e) => setContentData({ ...contentData, nextBatch: e.target.value })}
                  />
                </div>
                
                <Button onClick={saveContent} className="bg-slate-900 hover:bg-slate-800">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alumni Feedback Tab */}
          <TabsContent value="alumni" className="space-y-4" data-testid="alumni-tab-content">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <CardTitle>Alumni Feedback & Placement Audit</CardTitle>
                    <CardDescription>
                      {alumniFeedback.length} response{alumniFeedback.length !== 1 ? 's' : ''} · Manage Wall of Fame visibility and view private placement data
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleExportAlumniCsv}
                    variant="outline"
                    className="border-slate-300"
                    data-testid="alumni-export-csv-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {alumniFeedback.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No alumni feedback submissions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alumniFeedback.map((fb) => (
                      <div
                        key={fb.id}
                        className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                        data-testid={`alumni-row-${fb.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {fb.headshot_url ? (
                              <img src={fb.headshot_url} alt={fb.full_name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                <User className="h-6 w-6 text-slate-400" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-slate-900 truncate">{fb.full_name}</h4>
                                <Badge variant="outline" className="text-xs">{fb.batch_number}</Badge>
                                {fb.career_status === 'secured' && <Badge className="bg-green-100 text-green-700 text-xs">Placed</Badge>}
                                {fb.career_status === 'interviewed' && <Badge className="bg-amber-100 text-amber-700 text-xs">Interviewing</Badge>}
                                {fb.career_status === 'not_yet' && <Badge className="bg-slate-100 text-slate-600 text-xs">Not placed</Badge>}
                              </div>
                              <p className="text-sm text-slate-500 truncate">{fb.role_title || 'No role provided'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {fb.consent_wall_of_fame ? (
                              <Button
                                size="sm"
                                variant={fb.show_on_wall_of_fame ? 'default' : 'outline'}
                                className={fb.show_on_wall_of_fame ? 'bg-[#9c7a3c] hover:bg-[#8a6b35] text-white' : 'border-slate-300'}
                                onClick={() => handleToggleWallOfFame(fb)}
                                data-testid={`alumni-toggle-wall-${fb.id}`}
                              >
                                <Star className={`h-4 w-4 mr-1 ${fb.show_on_wall_of_fame ? 'fill-white' : ''}`} />
                                {fb.show_on_wall_of_fame ? 'On Wall' : 'Add to Wall'}
                              </Button>
                            ) : (
                              <Badge variant="outline" className="text-xs text-slate-400">No consent</Badge>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setSelectedAlumni(fb)} data-testid={`alumni-view-${fb.id}`}>
                              <Eye className="h-4 w-4 mr-1" /> View
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingAlumni({ ...fb })} data-testid={`alumni-edit-${fb.id}`}>
                              <PenSquare className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteAlumni(fb.id)} data-testid={`alumni-delete-${fb.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Admin Settings</CardTitle>
                <CardDescription>Configure admin panel preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="adminEmail">Admin Email</Label>
                  <Input id="adminEmail" type="email" value={mockData.adminUser.email} disabled />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email</Label>
                  <Input id="contactEmail" type="email" defaultValue={mockData.footer.email} />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contactPhone">Contact Phone</Label>
                  <Input id="contactPhone" type="tel" defaultValue={mockData.footer.phone} />
                </div>
                
                <Button className="bg-slate-900 hover:bg-slate-800">
                  Update Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Direct Message Modal */}
      {showDMModal && selectedStudentForDM && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="dm-modal">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 text-white">
              <h3 className="text-lg font-semibold flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Message Student
              </h3>
              <p className="text-sm text-white/70 mt-1">{selectedStudentForDM.name} ({selectedStudentForDM.email})</p>
            </div>
            <form onSubmit={handleSendDM} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dm_title">Subject *</Label>
                <Input
                  id="dm_title"
                  value={dmForm.title}
                  onChange={(e) => setDmForm({...dmForm, title: e.target.value})}
                  placeholder="Message subject..."
                  required
                  data-testid="dm-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dm_message">Message *</Label>
                <Textarea
                  id="dm_message"
                  value={dmForm.message}
                  onChange={(e) => setDmForm({...dmForm, message: e.target.value})}
                  placeholder="Type your message here..."
                  rows={5}
                  required
                  data-testid="dm-message-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  type="submit" 
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="send-dm-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDMModal(false)}
                  className="flex-1"
                  data-testid="cancel-dm-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alumni View Detail Modal */}
      {selectedAlumni && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="alumni-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-[#14202e] px-6 py-4 text-white flex items-center gap-3">
              {selectedAlumni.headshot_url ? (
                <img src={selectedAlumni.headshot_url} alt={selectedAlumni.full_name} className="w-12 h-12 rounded-full object-cover border border-[#9c7a3c]" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><User className="h-6 w-6" /></div>
              )}
              <div>
                <h3 className="text-lg font-serif">{selectedAlumni.full_name}</h3>
                <p className="text-sm text-white/60">{selectedAlumni.batch_number}</p>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-[#9c7a3c] mb-1">Course Feedback</p>
                <p className="text-slate-700 leading-relaxed">{selectedAlumni.course_feedback || '—'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-[#9c7a3c] mb-1">Career Status</p>
                  <p className="text-slate-700">{selectedAlumni.career_status || '—'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-[#9c7a3c] mb-1">Role Title</p>
                  <p className="text-slate-700">{selectedAlumni.role_title || '—'}</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="font-mono text-xs uppercase tracking-wider text-amber-700">Private (Admin Only)</p>
                <p className="text-slate-700"><span className="text-slate-500">Organization:</span> {selectedAlumni.organization_name || '—'}</p>
                <p className="text-slate-700"><span className="text-slate-500">Future Outlook:</span> {selectedAlumni.future_outlook || '—'}</p>
                <p className="text-slate-700"><span className="text-slate-500">Support Needed:</span> {selectedAlumni.support_needed || '—'}</p>
              </div>
              {selectedAlumni.video_url && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-[#9c7a3c] mb-1">Video Review</p>
                  <video src={selectedAlumni.video_url} controls className="w-full rounded-lg" />
                </div>
              )}
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">Wall consent: {selectedAlumni.consent_wall_of_fame ? 'Yes' : 'No'}</Badge>
                <Badge variant="outline">On Wall: {selectedAlumni.show_on_wall_of_fame ? 'Yes' : 'No'}</Badge>
                <Badge variant="outline">Show role: {selectedAlumni.display_role_publicly ? 'Yes' : 'No'}</Badge>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setSelectedAlumni(null)} data-testid="alumni-detail-close">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Alumni Edit Modal */}
      {editingAlumni && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="alumni-edit-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-[#14202e] px-6 py-4 text-white">
              <h3 className="text-lg font-serif">Edit Alumni Response</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editingAlumni.full_name || ''} onChange={(e) => setEditingAlumni({ ...editingAlumni, full_name: e.target.value })} data-testid="alumni-edit-name" />
              </div>
              <div className="space-y-2">
                <Label>Batch Number</Label>
                <Input value={editingAlumni.batch_number || ''} onChange={(e) => setEditingAlumni({ ...editingAlumni, batch_number: e.target.value })} data-testid="alumni-edit-batch" />
              </div>
              <div className="space-y-2">
                <Label>Role Title</Label>
                <Input value={editingAlumni.role_title || ''} onChange={(e) => setEditingAlumni({ ...editingAlumni, role_title: e.target.value })} data-testid="alumni-edit-role" />
              </div>
              <div className="space-y-2">
                <Label>Organization (Private)</Label>
                <Input value={editingAlumni.organization_name || ''} onChange={(e) => setEditingAlumni({ ...editingAlumni, organization_name: e.target.value })} data-testid="alumni-edit-org" />
              </div>
              <div className="space-y-2">
                <Label>Course Feedback</Label>
                <Textarea rows={4} value={editingAlumni.course_feedback || ''} onChange={(e) => setEditingAlumni({ ...editingAlumni, course_feedback: e.target.value })} data-testid="alumni-edit-feedback" />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editingAlumni.display_role_publicly} onChange={(e) => setEditingAlumni({ ...editingAlumni, display_role_publicly: e.target.checked })} />
                  Display role publicly
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editingAlumni.consent_wall_of_fame} onChange={(e) => setEditingAlumni({ ...editingAlumni, consent_wall_of_fame: e.target.checked })} />
                  Wall of Fame consent
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editingAlumni.show_on_wall_of_fame} onChange={(e) => setEditingAlumni({ ...editingAlumni, show_on_wall_of_fame: e.target.checked })} />
                  Show on Wall of Fame
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={handleUpdateAlumni} data-testid="alumni-edit-save">Save Changes</Button>
                <Button variant="outline" className="flex-1" onClick={() => setEditingAlumni(null)} data-testid="alumni-edit-cancel">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
